import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';
import { OAuthService } from 'angular-oauth2-oidc';
import { AlertController } from '@ionic/angular';
import { DataService } from '../data.service';
import {
  OfAtlag, OfIdoszak, OfJegy, atlagSzamol, atlagSzoveg, gyengeAtlag, jegyekKiegeszit,
} from './of-jegyek';

/** A képernyő nézetei: a hiányzások (az eredeti), a jegyek, a tanórai feljegyzések és az SNI / BTMN. */
type OfNezet = 'hianyzas' | 'elbiralas' | 'jegyek' | 'ikszek' | 'szakvelemeny' | 'minosithetoseg' | 'lemorzsolodas' | 'kesve';

/** Egy szakértői vélemény sora (backend/szakertoi-velemeny.js, MEZOK). */
interface OfSzakvelemeny {
  id?: number;
  tipus: string;
  bizottsag_nev?: string | null;
  bizottsag_cim?: string | null;
  velemeny_szam?: string | null;
  kelt?: string | null;
  felulvizsgalat?: number | boolean;
  kovetkezo_felulvizsgalat?: string | null;
  ervenyesseg?: string | null;
  fogyatekossag?: string | null;
  javaslat?: string | null;
  megjegyzes?: string | null;
  forras?: string;
}
interface OfSzvTanulo { oktazon: string; tanulo_nev: string; velemenyek: OfSzakvelemeny[]; kreta: OfSzakvelemeny[]; }

interface OfTargyOszlop { tantargy_id: string; tantargy_nev: string; tantargy_rovid: string; orarendi: boolean; }
interface OfJegyCella { evkozi: OfJegy[]; zaro: OfJegy[]; atlag: OfAtlag; atlagSzoveg: string; gyenge: boolean; }
interface OfJegySor {
  oktazon: string;
  tanulo_nev: string;
  cellak: Record<string, OfJegyCella>;
  jegyDb: number;
  osszAtlag: string;
  gyengeTargyak: number;
}
interface OfIkszTipus { kulcs: string; nev: string; ikon?: string | null; ikszes: boolean; forras: string; leiras?: string; }
interface OfIkszBejegyzes {
  forras: 'ora' | 'feljegyzes';
  kulcs: string;
  oktazon: string;
  datum: string;
  ido: string | null;
  ora: number | null;
  tantargy: string;
  cel: string;
  tanar_nev: string;
  megjegyzes: string;
  tema: string;
  kreta?: boolean;
  tanulo_nev?: string;
}

interface OfOsztaly {
  osztaly_id: string;
  osztaly_nev: string;
  evfolyam?: number;
  tanev?: number;
  intezmeny?: string;
  of_username?: string | null;
  of_nev?: string | null;
  /** A kérdező szerepe az osztályban: osztályfőnök vagy osztályfőnök-helyettes (a kiszolgálótól). */
  szerep?: 'of' | 'helyettes' | null;
}

interface OfTipus { id: number; nev: string; }

interface OfTanulo {
  oktazon: string;
  tanulo_nev: string;
  osszes: number;
  igazolt: number;
  igazolatlan: number;
  elbiralatlan: number;
  tipusok: Record<string, number>;
  /** Késésből adódó mulasztás (a fenti összegekben már benne van). */
  keses?: OfKeses;
  /** A tanévben kilépett tanuló utolsó jogviszony-napja (szürkén a lista végén). */
  kilepett?: string | null;
}

/** A késések percei és a teljes 45 percenként adódó órák – igazolt és igazolatlan külön. */
interface OfKeses {
  keses_db: number;
  perc_igazolatlan: number;
  perc_igazolt: number;
  ora_igazolatlan: number;
  ora_igazolt: number;
  ora_osszes: number;
  maradek_igazolatlan: number;
  maradek_igazolt: number;
  kovetkezo_orahoz: number;
}

/*  IGAZOLATLAN MULASZTÁS – ÉRTESÍTÉSEK (backend/mulasztas-ertesites.js). */
interface OfIglCsoport { kodok: string[]; nev: string; teljesitve: boolean; datum: string | null; }
interface OfIglTeendo {
  kulcs: string; fajta: string; kuszob: number | null; cim: string; jogalap: string; felelos: string;
  megjegyzes?: string; teljesitve: boolean; hianyzik: string[]; csoportok: OfIglCsoport[];
  nyomtatvany: { url: string; cim: string } | null;
  /** Több fajta is zárhatja (51. § (3) ismételt megkeresés: megkeresés VAGY „eredményes volt”). */
  fajtak?: string[];
}
interface OfIglErtesites {
  id: string; fajta: string; fajta_nev: string; kuszob: number | null; cimzett: string; cimzett_szoveg: string;
  cimzett_nev?: string | null; modja: string; mod_nev: string; datum: string; kiado?: string | null;
  megjegyzes?: string | null; visszavonva?: string | null; visszavonas_oka?: string | null; dokumentum_id?: string | null;
}
interface OfIglTanulo {
  oktazon: string; nev: string; osztaly: string; igazolatlan: number; igazolatlan_kesesbol: number;
  /** Egyéb foglalkozáson (napközi, tanulószoba, szakkör) – 2026-11-02 óta az 51. § küszöbébe számít. */
  igazolatlan_egyeb?: number;
  tankoteles: boolean; tankot_vege: string | null; kiskoru: boolean; kilepett: string | null;
  teendok: OfIglTeendo[]; nyitott: number; ertesitesek: OfIglErtesites[];
  megszunes: { tobb: boolean; megallapithato: boolean; ok: string; kilepes_ok_kod: string } | null;
}
interface OfIglForm {
  oktazon: string; fajta: string; kuszob: number | null; cim: string;
  cimzett: string; mod: string; datum: string; cimzett_nev: string; megjegyzes: string;
}

interface OfElbOra { id?: string; ora: number; tantargy: string; }
interface OfElbNap { datum: string; ora_db: number; orak: OfElbOra[]; }
interface OfElbTanulo {
  oktazon: string;
  tanulo_nev: string;
  /** Egyéni tanrend (magántanuló): a hiányzása innen törölhető is, nem csak igazolható. */
  egyeni_tanrend?: number;
  osszes_elbiralatlan: number;
  napok: OfElbNap[];
  kilepett?: string | null;
}


@Component({
  selector: 'app-osztalyfonok',
  templateUrl: './osztalyfonok.component.html',
  styleUrls: ['./osztalyfonok.component.scss'],
})
export class OsztalyfonokComponent implements OnInit {
  intezmeny = '';
  username = '';
  admin = false;
  tanev: number | null = null;

  osztalyok: OfOsztaly[] = [];
  kivOsztalyId = '';
  tipusok: OfTipus[] = [];
  /*  IGAZOLÁS IDŐSZAKRA (pl. kikérő). A részletező csak a már beírt hiányzásokat mutatja;
   *  ez előre is megadható, és a később beírt hiányzásokra is érvényes lesz. */
  idoszak: { oktazon: string | null; tol: string; ig: string; tipus: number | null } =
    { oktazon: null, tol: '', ig: '', tipus: null };
  idoszakMentes = false;
  tanulok: OfTanulo[] = [];
  expandedOkt: string | null = null;

  elbiralatlanTanulok: OfElbTanulo[] = [];
  expandedElbOkt: string | null = null;
  loadingElb = false;
  // A részletezőből történő tömeges igazoláshoz: a kijelölt napok és a választott típus.
  elbSelectedNapok = new Set<string>();
  elbIgazolasTipus: number | null = null;
  elbMentesFolyamatban = false;

  loadingOsztalyok = false;
  loadingOsszesito = false;
  hiba = '';

  constructor(private http: HttpClient, private data: DataService, private oauth: OAuthService,
              private alertCtrl: AlertController) {}

  async ngOnInit(): Promise<void> {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    this.sajatIntezmeny = si || null;
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');

    const claims: any = this.data.belepoKilet(this.oauth.getIdentityClaims()) || {};
    this.username = String(claims.preferred_username || claims.username || '').trim();

    await this.loadOsztalyok();
  }

  get intezmenyPath(): string {
    return encodeURI(this.intezmeny);
  }

  async loadOsztalyok(): Promise<void> {
    if (!this.intezmeny) { this.hiba = 'Hiányzó intézmény.'; return; }
    this.loadingOsztalyok = true;
    this.hiba = '';
    const params = new URLSearchParams();
    if (this.username) params.set('username', this.username);
    const suffix = params.toString() ? `?${params.toString()}` : '';

    this.http.get<any>(`/api/of/osztalyok/${this.intezmenyPath}${suffix}`).subscribe({
      next: (rsp) => {
        this.osztalyok = rsp?.osztalyok || [];
        // Az admin-jogot a kiszolgáló dönti el (a tokenből), nem a böngészőben tárolt jogkör.
        this.admin = !!rsp?.admin;
        this.tanev = rsp?.tanev ?? null;
        this.loadingOsztalyok = false;
        if (this.osztalyok.length) {
          this.kivOsztalyId = this.osztalyok[0].osztaly_id;
          this.loadOsszesito();
          this.loadElbiralatlan();
          this.loadIgazolasok();
          this.loadIgl();
          //  A várólista minden osztályra szól, ezért osztályválasztástól függetlenül.
          this.loadVarolista();
        } else {
          this.tanulok = [];
          this.elbiralatlanTanulok = [];
        }
      },
      error: (err) => {
        this.loadingOsztalyok = false;
        this.hiba = err?.error?.error || 'Nem sikerült betölteni az osztályokat.';
        this.data.sendToast(this.hiba, 'error');
      },
    });
  }

  onOsztalyChange(): void {
    this.expandedOkt = null;
    this.expandedElbOkt = null;
    this.loadOsszesito();
    this.loadElbiralatlan();
    this.loadIgazolasok();
    this.loadIgl();
    //  A jegy- és a feljegyzés-nézet csak akkor töltődik, ha éppen látszik.
    this.nezetFrissit();
  }

  /*  A SZÜLŐK BEKÜLDÖTT IGAZOLÁSAI.
   *
   *  Az elbírálási lista intézményi és állapotra szűrt; az osztályfőnöknek viszont épp
   *  az kell, hogy a saját osztályánál lássa, ki mit küldött be és mi lett vele – az
   *  elutasítottat és a jóváhagyottat is. Enélkül nem tud válaszolni a leggyakoribb
   *  kérdésre: „a szülő beküldte-e egyáltalán?" */
  igazolasok: any[] = [];
  loadingIgazolasok = false;

  loadIgazolasok(): void {
    if (!this.kivOsztalyId) { this.igazolasok = []; return; }
    this.loadingIgazolasok = true;
    const ev = this.tanev ? `?ev=${this.tanev}` : '';
    this.http.get<any[]>(
      `/api/hianyzas-igazolas/osztaly/${encodeURIComponent(this.kivOsztalyId)}/${this.intezmenyPath}${ev}`)
      .subscribe({
        next: (rsp) => { this.igazolasok = Array.isArray(rsp) ? rsp : []; this.loadingIgazolasok = false; },
        error: () => { this.igazolasok = []; this.loadingIgazolasok = false; },
      });
  }

  /*  A melléklet a KISZOLGÁLÓN keresztül nyílik meg: a fájltárhoz a böngészőnek nincs
   *  köze, és így a jogosultság is egy helyen dől el. */
  csatolmanyUrl(c: any): string {
    return `/api/hianyzas-igazolas/csatolmany-fajl/${c.id}/${this.intezmenyPath}`;
  }

  /*  A fájl a TOKENES megnyitóval nyílik. A linkes megnyitás (`<a href>`, `window.open`)
   *  nem viszi a belépési jegyet, a kiszolgáló pedig – helyesen – nem ad ki fájlt kilét
   *  nélkül; emiatt esett ki 2026-09-23-án a csatolmányok megnyitása. */
  csatolmanyNyit(c: any) { this.data.megnyitFajl(this.csatolmanyUrl(c), c?.nev || 'Melléklet'); }

  /** Emberi méret – a nyers bájtszámból nem derül ki, mekkora a fájl. */
  meret(b: number): string {
    const n = Number(b) || 0;
    if (!n) return '–';
    return n < 1024 * 1024 ? `${Math.round(n / 1024)} kB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
  }

  /** Az állapot emberi neve és színe – a nyers kulcsot senki nem olvassa. */
  igazolasAllapot(s: string): { szoveg: string; szin: string } {
    const k = String(s || '').toLowerCase();
    if (k === 'igazolt' || k === 'jovahagyva') return { szoveg: 'elfogadva', szin: 'success' };
    if (k === 'elutasitva') return { szoveg: 'elutasítva', szin: 'danger' };
    //  Az osztályfőnök tévesnek találta és visszavonta – a nap újra igazolandó,
    //  és visszakerült a szülő keretébe.
    if (k === 'visszavonva') return { szoveg: 'visszavonva', szin: 'medium' };
    if (k === 'engedelyre_var') return { szoveg: 'igazgatói engedélyre vár', szin: 'warning' };
    if (k === 'elbiralasra_var') return { szoveg: 'az osztályfőnök elbírálására vár', szin: 'warning' };
    return { szoveg: k || '–', szin: 'medium' };
  }

  /*  A MELLÉKELT ORVOSI / HIVATALOS IGAZOLÁS ELBÍRÁLÁSA – és az ÁTMINŐSÍTÉS.
   *
   *  A szülő a dokumentumos igazolást (orvosi, nyelvvizsga) a keret terhelése nélkül küldi
   *  be; itt dől el a papír alapján, hogy felkerül-e az órákra, és milyen típussal. Egy
   *  szülői igazolással már elfogadott nap pedig átminősíthető, ha utólag megjött az
   *  orvosi papír – ez adja vissza a napot a szülői keretbe. */
  readonly elbiralasTipusok = [
    { id: 1, nev: 'Orvosi igazolás' },
    { id: 4, nev: 'Hivatalos távollét' },
    { id: 3, nev: 'Iskolaérdekű távollét' },
    { id: 5, nev: 'Kikérő / Igazgatói engedély' },
    { id: 2, nev: 'Gondviselői igazolás' },
    //  20/2012. EMMI r. 51. § (2) e) – 7–8. és 10–12. évfolyam, tanévente 2 alkalom; a kiszolgáló ellenőrzi.
    { id: 7, nev: 'Pályaválasztási célú távollét' },
  ];
  /*  AMI AZ OSZTÁLYFŐNÖK ASZTALÁN VAN – az ÖSSZES osztályára, osztályválasztástól
   *  függetlenül. Enélkül az elbírálásra váró orvosi igazolás csak akkor került a szeme
   *  elé, ha épp azt az osztályt nyitotta meg, és végiggörgette a beküldött igazolásokat.
   *  A szülő közben azt látta, hogy beadta, és nem történik semmi. */
  varolista: any[] = [];
  varolistaFut = false;

  loadVarolista(): void {
    if (!this.intezmenyPath) { this.varolista = []; return; }
    this.varolistaFut = true;
    this.http.get<any>(`/api/hianyzas-igazolas/elbiralando/${this.intezmenyPath}`).subscribe({
      next: (rsp) => { this.varolista = Array.isArray(rsp?.tetelek) ? rsp.tetelek : []; this.varolistaFut = false; },
      error: () => { this.varolista = []; this.varolistaFut = false; },
    });
  }

  igDontesTipus: { [id: string]: number } = {};
  igDontesIndok: { [id: string]: string } = {};
  igDontesFolyamatban = false;

  igElbiralhato(i: any): boolean {
    return String(i?.statusz) === 'elbiralasra_var';
  }

  /*  A SZÜLŐ UTÓLAG TÖLTÖTT FEL PAPÍRT egy már elbírált naphoz. A nap rendben van –
   *  itt nem róla, hanem a DOKUMENTUMRÓL kell dönteni: elfogadom (és akkor át is
   *  minősíthetem a napot orvosira), vagy nem fogadom el (a nap marad, ami volt). */
  igPapirElbiralando(i: any): boolean {
    return Number(i?.ujra_elbiralando) === 1 && !this.igElbiralhato(i);
  }

  igAtminosithato(i: any): boolean {
    return String(i?.statusz) === 'igazolt' && Number(i?.tipus || 2) === 2;
  }

  /** Van-e bármilyen döntenivaló ezen a soron – ez nyitja a döntés-blokkot. */
  igVanDontes(i: any): boolean {
    return this.igElbiralhato(i) || this.igPapirElbiralando(i) || this.igAtminosithato(i);
  }

  igDontes(i: any, dontes: 'jovahagy' | 'elutasit'): void {
    if (!i?.id || this.igDontesFolyamatban) return;
    const indok = String(this.igDontesIndok[i.id] || '').trim();
    if (dontes === 'elutasit' && !indok) {
      this.data.sendToast('Elutasításnál az indoklás kötelező – a szülő ezt fogja látni.', 'warning');
      return;
    }
    //  Elbírálásnál a szülő által választott típus az alap; átminősítésnél választani kell.
    const tipus = Number(this.igDontesTipus[i.id]) || (this.igElbiralhato(i) ? Number(i.tipus) : 0) || undefined;
    /*  Az utólag feltöltött papírnál a „marad, ami volt" is érvényes válasz: nem
     *  kötelező átminősíteni, elég megnézni. Az átminősítésnél viszont muszáj
     *  megmondani, mivé – különben a jóváhagyás nem jelentene semmit. */
    if (dontes === 'jovahagy' && this.igAtminosithato(i) && !this.igPapirElbiralando(i)
        && (!tipus || tipus === 2)) {
      this.data.sendToast('Válaszd ki, milyen igazolássá minősíted át a napot.', 'warning');
      return;
    }
    this.igDontesFolyamatban = true;
    this.http.post<any>(`/api/hianyzas-igazolas/engedely/${i.id}`, {
      dontes, indok: indok || null, tipus, elbiralo: this.username || null,
    }).subscribe({
      next: () => {
        this.igDontesFolyamatban = false;
        this.data.sendToast(
          dontes === 'jovahagy'
            ? (this.igPapirElbiralando(i) && !this.igDontesTipus[i.id]
              ? 'Rendben – a dokumentum elbírálva, a nap változatlan.'
              : 'Elfogadva, az igazolás felkerült az órákra.')
            : 'Elutasítva.');
        this.loadIgazolasok();
        this.loadVarolista();
        this.loadOsszesito();
        this.loadElbiralatlan();
      },
      error: (e) => {
        this.igDontesFolyamatban = false;
        this.data.sendToast(e?.error?.error || 'A döntés nem sikerült.', 'error');
      },
    });
  }

  private osztalyQuery(): string {
    const params = new URLSearchParams();
    params.set('osztaly', this.kivOsztalyId);
    if (this.tanev) params.set('tanev', String(this.tanev));
    return params.toString();
  }

  loadOsszesito(): void {
    if (!this.kivOsztalyId) return;
    this.loadingOsszesito = true;

    this.http.get<any>(`/api/of/osszesito/${this.intezmenyPath}?${this.osztalyQuery()}`).subscribe({
      next: (rsp) => {
        this.tipusok = rsp?.tipusok || [];
        this.tanulok = rsp?.tanulok || [];
        this.loadingOsszesito = false;
      },
      error: (err) => {
        this.loadingOsszesito = false;
        this.data.sendToast(err?.error?.error || 'Nem sikerült betölteni a hiányzás-összesítőt.', 'error');
      },
    });
  }

  loadElbiralatlan(): void {
    if (!this.kivOsztalyId) return;
    this.loadingElb = true;

    this.http.get<any>(`/api/of/elbiralatlan/${this.intezmenyPath}?${this.osztalyQuery()}`).subscribe({
      next: (rsp) => {
        this.elbiralatlanTanulok = rsp?.tanulok || [];
        this.loadingElb = false;
      },
      error: (err) => {
        this.loadingElb = false;
        this.data.sendToast(err?.error?.error || 'Nem sikerült betölteni az elbírálatlan hiányzásokat.', 'error');
      },
    });
  }

  /*  EGY TANULÓ RÉSZLETES MULASZTÁSA.
   *
   *  A sor kinyitva eddig csak a típusonkénti végösszeget mutatta. A gyakori kérdés
   *  viszont az, hogy MIKOR és MIBŐL hiányzott – ezt eddig a heti rácsban kellett
   *  hétről hétre végiglapozni. A részletet csak kinyitáskor kérjük le: egy osztálynyi
   *  tanulóra előre betölteni fölösleges munka volna. */
  tanuloReszlet: any = null;
  tanuloReszletFut = false;
  tanuloReszletHiba = '';

  /*  TANULÓ-VÁLASZTÓ – hogy a napra bontott összesítő MEGTALÁLHATÓ legyen.
   *
   *  Eddig csak a névsor egy sorára kattintva nyílt ki, amit senki nem talált meg:
   *  a képernyőn semmi nem mutatta, hogy a sor mögött van még valami. A választó
   *  ugyanazt nyitja ki, csak névről is elérhetően. */
  get valasztottTanuloOkt(): string { return this.expandedOkt ? String(this.expandedOkt) : ''; }
  set valasztottTanuloOkt(ertek: string) {
    const t = this.tanulok.find((x) => String(x.oktazon) === String(ertek));
    if (!t) { this.expandedOkt = null; this.tanuloReszlet = null; return; }
    if (this.expandedOkt !== t.oktazon) this.toggleRow(t);
  }

  toggleRow(t: OfTanulo): void {
    const nyit = this.expandedOkt !== t.oktazon;
    this.expandedOkt = nyit ? t.oktazon : null;
    this.tanuloReszlet = null;
    this.tanuloReszletHiba = '';
    if (nyit) this.tanuloReszletBetolt(t);
  }

  private tanuloReszletBetolt(t: OfTanulo): void {
    this.tanuloReszletFut = true;
    const p = new URLSearchParams({ oktazon: String(t.oktazon) });
    if (this.tanev) p.set('tanev', String(this.tanev));
    this.http.get<any>(`/api/of/tanulo/${this.intezmenyPath}?${p.toString()}`).subscribe({
      next: (rsp) => { this.tanuloReszlet = rsp || null; this.tanuloReszletFut = false; },
      error: (e) => {
        this.tanuloReszlet = null;
        this.tanuloReszletHiba = e?.error?.error || 'A tanuló mulasztásait nem sikerült lekérni.';
        this.tanuloReszletFut = false;
      },
    });
  }

  /* ══════════════════════════════════════════════════════════════════════
   *  MULASZTÁS-KIMUTATÁS LETÖLTÉSE (Excel)
   *
   *  Az iskolának rendszeresen kell napra bontott kimutatás – tipikusan a gyermekjóléti
   *  szolgálatnak szóló levélhez, ha az igazolatlan órák száma túllépi a 10-et: MELY
   *  NAPOKON, melyik órán, miből hiányzott (europa2000, Libus Ágnes, 2026-09-28). A
   *  képernyőn ez eddig is látszott tanulónként, letölteni nem lehetett.
   *
   *  Ugyanazt az adatot használjuk, mint a képernyő (`/api/of/osszesito` és
   *  `/api/of/tanulo`), tehát a kimutatás nem térhet el attól, amit az osztályfőnök lát.
   *  Két munkalap: az osztály összesítője és a napi/órai bontás. A „csak igazolatlan”
   *  változat a bontásban csak az igazolatlan órákat hozza, és csak azokat a tanulókat,
   *  akiknek van ilyen. */
  letoltesFut = false;

  async mulasztasLetoltes(csakIgazolatlan: boolean, egyTanulo?: OfTanulo): Promise<void> {
    if (this.letoltesFut) return;
    const osztaly = this.osztalyok.find((o) => o.osztaly_id === this.kivOsztalyId);
    const kor = egyTanulo ? [egyTanulo]
      : this.tanulok.filter((t) => !csakIgazolatlan || Number(t.igazolatlan) > 0);
    if (!kor.length) {
      this.data.sendToast(csakIgazolatlan ? 'Az osztályban nincs igazolatlan mulasztás.' : 'Nincs letölthető tanuló.', 'info');
      return;
    }
    this.letoltesFut = true;
    try {
      const allapotNev: Record<string, string> = { igazolt: 'igazolt', igazolatlan: 'igazolatlan', elbiralatlan: 'elbírálatlan' };
      const bontas: any[] = [];
      for (const t of kor) {
        const p = new URLSearchParams({ oktazon: String(t.oktazon) });
        if (this.tanev) p.set('tanev', String(this.tanev));
        const r: any = await firstValueFrom(this.http.get<any>(`/api/of/tanulo/${this.intezmenyPath}?${p.toString()}`));
        const napok = [...(r?.napok || [])].sort((a: any, b: any) => String(a.datum).localeCompare(String(b.datum)));
        for (const n of napok) {
          for (const o of n.orak || []) {
            if (csakIgazolatlan && o.allapot !== 'igazolatlan') continue;
            bontas.push({
              'Tanuló': t.tanulo_nev, 'Oktatási azonosító': String(t.oktazon),
              'Dátum': String(n.datum || '').slice(0, 10), 'Óra': o.ora, 'Óraszám': o.ora_db,
              'Tantárgy': o.tantargy || '', 'Osztály / csoport': o.cel || '',
              'Állapot': allapotNev[o.allapot] || o.allapot, 'Igazolás típusa': o.tipus_nev || '',
            });
          }
        }
      }
      const osszesito = kor.map((t, i) => ({
        '#': i + 1, 'Tanuló': t.tanulo_nev, 'Oktatási azonosító': String(t.oktazon),
        'Összes óra': t.osszes, 'Igazolt': t.igazolt, 'Igazolatlan': t.igazolatlan, 'Elbírálatlan': t.elbiralatlan,
        'Ebből késésből (óra)': t.keses?.ora_osszes || 0,
        'Igazolatlan napok': new Set(bontas.filter((b) => b['Oktatási azonosító'] === String(t.oktazon) && b['Állapot'] === 'igazolatlan').map((b) => b['Dátum'])).size || (csakIgazolatlan ? 0 : ''),
      }));
      const wb = XLSX.utils.book_new();
      const lap1 = XLSX.utils.json_to_sheet(osszesito);
      lap1['!cols'] = [{ wch: 4 }, { wch: 28 }, { wch: 14 }, { wch: 10 }, { wch: 9 }, { wch: 11 }, { wch: 12 }, { wch: 18 }, { wch: 16 }];
      XLSX.utils.book_append_sheet(wb, lap1, 'Összesítő');
      const lap2 = XLSX.utils.json_to_sheet(bontas.length ? bontas : [{ 'Tanuló': 'Nincs mulasztás a kiválasztott szűréssel.' }]);
      lap2['!cols'] = [{ wch: 28 }, { wch: 14 }, { wch: 11 }, { wch: 5 }, { wch: 8 }, { wch: 28 }, { wch: 22 }, { wch: 12 }, { wch: 22 }];
      XLSX.utils.book_append_sheet(wb, lap2, csakIgazolatlan ? 'Igazolatlan órák' : 'Napi bontás');
      const tisztit = (v: string) => String(v || '').replace(/[^\p{L}\p{N}]+/gu, '_').replace(/^_+|_+$/g, '');
      const nev = [
        csakIgazolatlan ? 'igazolatlan' : 'mulasztas',
        egyTanulo ? tisztit(egyTanulo.tanulo_nev) : tisztit(osztaly?.osztaly_nev || 'osztaly'),
        new Date().toISOString().slice(0, 10),
      ].join('_');
      XLSX.writeFile(wb, `${nev}.xlsx`);
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A kimutatást nem sikerült elkészíteni.', 'error');
    } finally {
      this.letoltesFut = false;
    }
  }

  /** A napon belüli órák rövid, olvasható összefoglalója. */
  reszletAllapotSzin(allapot: string): string {
    if (allapot === 'igazolt') return 'success';
    if (allapot === 'igazolatlan') return 'danger';
    return 'warning';
  }

  /* ══════════════════════════════════════════════════════════════════════
   *  TÉVESEN ELBÍRÁLT NAP VISSZAVONÁSA – vissza „igazolandó" állapotba.
   *
   *  Az elbírálás eddig egyirányú volt: ami egyszer igazolt vagy igazolatlan lett,
   *  azon az osztályfőnök nem tudott változtatni (a tömeges igazolás szándékosan csak
   *  az elbírálatlan órákhoz nyúl). Rossz napra vagy rossz típussal kattintani viszont
   *  mindennapos – kellett egy visszaút.
   *
   *  KÉTLÉPÉSES, mert nem visszavonható kattintás: a nap döntései eltűnnek, és ha a
   *  napra szülői igazolás is tartozott, az is visszavonásra kerül (különben a rendszer
   *  magától visszaírná). Ezt a megerősítő sor ki is mondja.
   * ═════════════════════════════════════════════════════════════════════ */
  visszavonNap: string | null = null;
  visszavonIndok = '';
  visszavonFolyamatban = false;

  /** Hány órán van egyáltalán döntés ezen a napon – enélkül nincs mit visszavonni. */
  napElbiraltOrak(n: any): number {
    return (n?.orak || []).filter((o: any) => o?.allapot === 'igazolt' || o?.allapot === 'igazolatlan').length;
  }

  visszavonKerdes(n: any): void {
    this.visszavonNap = this.visszavonNap === n?.datum ? null : (n?.datum || null);
    this.visszavonIndok = '';
  }

  visszavon(n: any): void {
    const oktazon = this.expandedOkt ? String(this.expandedOkt) : '';
    const datum = String(n?.datum || '');
    if (!oktazon || !datum || this.visszavonFolyamatban) return;
    this.visszavonFolyamatban = true;
    this.http.post<any>(`/api/of/visszavon/${this.intezmenyPath}`, {
      osztaly: this.kivOsztalyId,
      tanev: this.tanev,
      oktazon,
      datumok: [datum],
      indok: String(this.visszavonIndok || '').trim() || null,
    }).subscribe({
      next: (rsp) => {
        this.visszavonFolyamatban = false;
        this.visszavonNap = null;
        this.visszavonIndok = '';
        const szuloi = Number(rsp?.szuloi_igazolasok || 0);
        this.data.sendToast(
          `${datum}: ${rsp?.orak ?? 0} óra visszakerült igazolandóba`
          + (szuloi ? ' – a napra beadott szülői igazolást is visszavontuk.' : '.'),
          'success');
        const t = this.tanulok.find((x) => String(x.oktazon) === oktazon);
        if (t) this.tanuloReszletBetolt(t);
        this.loadOsszesito();
        this.loadElbiralatlan();
        this.loadIgazolasok();
      },
      error: (e) => {
        this.visszavonFolyamatban = false;
        this.data.sendToast(e?.error?.error || 'A visszavonás nem sikerült.', 'error');
      },
    });
  }

  /* ══════════════════════════════════════════════════════════════════════
   *  TÉVESEN RÖGZÍTETT HIÁNYZÁS TÖRLÉSE (hibajegy #1057).
   *
   *  A visszavonás a DÖNTÉST veszi le, a hiányzás tényét nem. Ha viszont a tanár rossz
   *  tanulót jelölt hiányzónak, maga a hiányzás a hibás: ezt az osztályfőnök (helyettes,
   *  admin) óránként vagy egy egész napra törölheti – a tanuló lekerül az óra hiányzói
   *  közül, a rá vonatkozó igazolás-bejegyzéssel együtt. A szülői igazolás-sor marad.
   *
   *  Megerősítő ablakkal, indokkal: a törlés nem visszavonható kattintás (visszaállítani
   *  csak a naplóban újra bejelölve lehet), és a kiszolgáló naplózza, ki és miért törölt.
   * ═════════════════════════════════════════════════════════════════════ */
  hianyzasTorlesFut = false;

  /** Törölhet-e hiányzást: admin, vagy a kiválasztott osztály ofője / helyettese. */
  get hianyzasTorolheto(): boolean {
    if (this.admin) return true;
    return !!this.osztalyok.find((o) => o.osztaly_id === this.kivOsztalyId)?.szerep;
  }

  async hianyzasTorles(n: any, o?: any): Promise<void> {
    const oktazon = this.expandedOkt ? String(this.expandedOkt) : '';
    const orak = (o ? [o] : (n?.orak || [])).map((x: any) => String(x?.id ?? '')).filter(Boolean);
    if (!oktazon || !orak.length || this.hianyzasTorlesFut) return;
    const mit = o
      ? `${n.datum} – ${o.ora}. óra (${o.tantargy || 'óra'})`
      : `${n.datum} – a nap mind a(z) ${orak.length} órája`;
    const alert = await this.alertCtrl.create({
      header: 'Hiányzás törlése',
      message: `${mit}: a tanuló lekerül az óra hiányzói közül, a rá vonatkozó igazolással együtt. `
        + 'Csak tévesen rögzített hiányzásnál használd – visszaállítani a naplóban újra bejelölve lehet.',
      inputs: [{ name: 'indok', type: 'text', placeholder: 'Indok (nem kötelező), pl. jelen volt' }],
      buttons: [
        { text: 'Mégsem', role: 'cancel' },
        { text: 'Törlés', role: 'destructive' },
      ],
    });
    await alert.present();
    const { role, data } = await alert.onDidDismiss();
    if (role !== 'destructive') return;
    const indok = String(data?.values?.indok || '').trim() || null;

    this.hianyzasTorlesFut = true;
    this.http.post<any>(`/api/of/hianyzas-torles/${this.intezmenyPath}`, { oktazon, orak, indok }).subscribe({
      next: (rsp) => {
        this.hianyzasTorlesFut = false;
        const db = Number(rsp?.torolve || 0);
        this.data.sendToast(db ? `${n.datum}: ${db} óra hiányzása törölve.` : 'Nem volt törölhető hiányzás.',
          db ? 'success' : 'warning');
        const t = this.tanulok.find((x) => String(x.oktazon) === oktazon);
        if (t) this.tanuloReszletBetolt(t);
        this.loadOsszesito();
        this.loadElbiralatlan();
      },
      error: (e) => {
        this.hianyzasTorlesFut = false;
        this.data.sendToast(e?.error?.error || 'A hiányzás törlése nem sikerült.', 'error');
      },
    });
  }

  isExpanded(t: OfTanulo): boolean {
    return this.expandedOkt === t.oktazon;
  }

  toggleElb(t: OfElbTanulo): void {
    this.expandedElbOkt = this.expandedElbOkt === t.oktazon ? null : t.oktazon;
    // Új tanuló kibontásakor tiszta lappal indul a kijelölés.
    this.elbSelectedNapok.clear();
    this.elbIgazolasTipus = null;
  }

  isElbExpanded(t: OfElbTanulo): boolean {
    return this.expandedElbOkt === t.oktazon;
  }

  toggleNap(datum: string, ev: any): void {
    if (ev?.detail?.checked) this.elbSelectedNapok.add(datum);
    else this.elbSelectedNapok.delete(datum);
  }

  isNapSelected(datum: string): boolean {
    return this.elbSelectedNapok.has(datum);
  }

  toggleMindenNap(t: OfElbTanulo, ev: any): void {
    if (ev?.detail?.checked) t.napok.forEach((n) => this.elbSelectedNapok.add(n.datum));
    else this.elbSelectedNapok.clear();
  }

  mindenNapKijelolve(t: OfElbTanulo): boolean {
    return t.napok.length > 0 && t.napok.every((n) => this.elbSelectedNapok.has(n.datum));
  }

  mentElbIgazolas(t: OfElbTanulo): void {
    if (!this.elbSelectedNapok.size || this.elbIgazolasTipus == null) return;
    const body = {
      osztaly: this.kivOsztalyId,
      tanev: this.tanev,
      oktazon: t.oktazon,
      tipus: this.elbIgazolasTipus,
      datumok: Array.from(this.elbSelectedNapok),
    };
    this.elbMentesFolyamatban = true;
    this.http.post<any>(`/api/of/igazol/${this.intezmenyPath}`, body).subscribe({
      next: (rsp) => {
        this.elbMentesFolyamatban = false;
        /*  A KIMARADT ÓRÁKAT IS KIMONDJUK. Ami már MÁS hivatalos igazolást kapott
         *  (pl. orvosit), azt nem írjuk felül – de akkor a „N óra mentve" szám kevesebb,
         *  mint amit a felhasználó várt, és ok nélkül az hibának látszik. */
        const kimaradt = Number(rsp?.kihagyott_orak || 0);
        this.data.sendToast(`${t.tanulo_nev}: ${rsp?.orak ?? 0} óra igazolása mentve.`
          + (kimaradt ? ` ${kimaradt} óra változatlan maradt: azokon már más igazolás áll `
            + '(visszavonás után átírható).' : ''),
        kimaradt ? 'warning' : 'success');
        this.expandedElbOkt = null;
        this.elbSelectedNapok.clear();
        this.elbIgazolasTipus = null;
        // Frissítjük az összesítőt és a részletezőt is, hogy az új állapot látszódjon.
        this.loadOsszesito();
        this.loadElbiralatlan();
      },
      error: (err) => {
        this.elbMentesFolyamatban = false;
        this.data.sendToast(err?.error?.error || 'Nem sikerült menteni az igazolásokat.', 'error');
      },
    });
  }

  /* ══════════════════════════════════════════════════════════════════════
   *  MAGÁNTANULÓ HIÁNYZÁSÁNAK TÖRLÉSE AZ ELBÍRÁLATLAN-RÉSZLETEZŐBŐL
   *  (Fejér Orsolya, Európa 2000, 2026-10-02: „magántanulónál ofő tudjon törölni, ne
   *  csak igazolni").
   *
   *  Az egyéni tanrendű tanuló nem jár órára, a névsorban mégis ott áll – a tanárok
   *  hiányzónak jelölik, és ez itt elbírálatlanként gyűlik. Nem igazolni kell, hanem
   *  törölni: a kijelölt napok összes hiányzását egyben, vagy óránként. A kiszolgáló
   *  ellenőrzi, hogy a tanuló a napokon tényleg egyéni tanrendű, és hogy a kérő az
   *  osztályfőnöke, a helyettese vagy admin; minden törlést naplóz.
   * ═════════════════════════════════════════════════════════════════════ */
  elbTorlesFut = false;

  async magantanuloHianyzasTorles(t: OfElbTanulo, n?: OfElbNap, o?: OfElbOra): Promise<void> {
    if (!+(t.egyeni_tanrend || 0) || this.elbTorlesFut) return;
    const datumok = o ? [] : (n ? [n.datum] : Array.from(this.elbSelectedNapok));
    const orak = o?.id ? [String(o.id)] : [];
    if (!datumok.length && !orak.length) return;
    const mit = o
      ? `${n?.datum} – ${o.ora}. óra (${o.tantargy || 'óra'})`
      : `${datumok.length} nap (${datumok.slice().sort().join(', ')}) összes hiányzása`;
    const alert = await this.alertCtrl.create({
      header: 'Magántanuló hiányzásának törlése',
      message: `${t.tanulo_nev}: ${mit}. A tanuló lekerül az órák hiányzói közül, a rá vonatkozó `
        + 'igazolással együtt. Visszaállítani a naplóban újra bejelölve lehet.',
      inputs: [{ name: 'indok', type: 'text', placeholder: 'Indok (nem kötelező), pl. egyéni tanrend' }],
      buttons: [
        { text: 'Mégsem', role: 'cancel' },
        { text: 'Törlés', role: 'destructive' },
      ],
    });
    await alert.present();
    const { role, data } = await alert.onDidDismiss();
    if (role !== 'destructive') return;
    const indok = String(data?.values?.indok || '').trim() || 'Magántanuló (egyéni tanrend)';

    this.elbTorlesFut = true;
    const body = orak.length ? { oktazon: t.oktazon, orak, indok } : { oktazon: t.oktazon, datumok, indok };
    this.http.post<any>(`/api/of/hianyzas-torles/${this.intezmenyPath}`, body).subscribe({
      next: (rsp) => {
        this.elbTorlesFut = false;
        const db = Number(rsp?.torolve || 0);
        this.data.sendToast(db ? `${t.tanulo_nev}: ${db} óra hiányzása törölve.` : 'Nem volt törölhető hiányzás.',
          db ? 'success' : 'warning');
        if (!o) this.elbSelectedNapok.clear();
        this.loadOsszesito();
        this.loadElbiralatlan();
      },
      error: (e) => {
        this.elbTorlesFut = false;
        this.data.sendToast(e?.error?.error || 'A hiányzás törlése nem sikerült.', 'error');
      },
    });
  }

  /** Hétköznapok a két dátum között (a hétvégén nincs óra, felesleges megjegyezni). */
  private idoszakNapjai(tol: string, ig: string): string[] {
    const napok: string[] = [];
    const d = new Date(`${tol}T12:00:00`);
    const vege = new Date(`${ig}T12:00:00`);
    while (d <= vege && napok.length < 92) {
      const hetnap = d.getDay();
      if (hetnap !== 0 && hetnap !== 6) {
        const p = (n: number) => String(n).padStart(2, '0');
        napok.push(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`);
      }
      d.setDate(d.getDate() + 1);
    }
    return napok;
  }

  mentIdoszak(): void {
    const { oktazon, tol, ig, tipus } = this.idoszak;
    if (!oktazon || !tol || !ig || tipus == null) return;
    if (ig < tol) { this.data.sendToast('A „meddig” nem lehet korábbi a „mettől”-nél.', 'warning'); return; }
    const datumok = this.idoszakNapjai(tol, ig);
    if (!datumok.length) { this.data.sendToast('Az időszakban nincs tanítási (hét)nap.', 'warning'); return; }
    const nev = this.tanulok.find((t) => t.oktazon === oktazon)?.tanulo_nev || '';
    this.idoszakMentes = true;
    this.http.post<any>(`/api/of/igazol/${this.intezmenyPath}`, {
      osztaly: this.kivOsztalyId, tanev: this.tanev, oktazon, tipus, datumok,
    }).subscribe({
      next: (rsp) => {
        this.idoszakMentes = false;
        this.data.sendToast(`${nev}: ${tol} – ${ig} igazolva. A már beírt hiányzásokból ${rsp?.orak ?? 0} óra `
          + 'igazolódott; a később beírt hiányzások ezekre a napokra maguktól igazoltak lesznek.', 'success');
        this.idoszak = { oktazon: null, tol: '', ig: '', tipus: null };
        this.loadOsszesito();
        this.loadElbiralatlan();
      },
      error: (err) => {
        this.idoszakMentes = false;
        this.data.sendToast(err?.error?.error || 'Nem sikerült menteni az igazolást.', 'error');
      },
    });
  }

  tipusErtek(t: OfTanulo, tipusId: number): number {
    return t?.tipusok?.[String(tipusId)] ?? 0;
  }

  get aktivOsztaly(): OfOsztaly | undefined {
    return this.osztalyok.find((o) => o.osztaly_id === this.kivOsztalyId);
  }

  // Összegző sor (osztály szintű) – a táblázat lábához
  get osszesenOsszes(): number { return this.tanulok.reduce((s, t) => s + (t.osszes || 0), 0); }
  get osszesenIgazolt(): number { return this.tanulok.reduce((s, t) => s + (t.igazolt || 0), 0); }
  get osszesenIgazolatlan(): number { return this.tanulok.reduce((s, t) => s + (t.igazolatlan || 0), 0); }
  get osszesenElbiralatlan(): number { return this.tanulok.reduce((s, t) => s + (t.elbiralatlan || 0), 0); }
  get osszesenKesesOra(): number { return this.tanulok.reduce((s, t) => s + (t.keses?.ora_osszes || 0), 0); }
  get osszesenKesesPerc(): number { return this.tanulok.reduce((s, t) => s + this.kesesPerc(t.keses), 0); }

  /** Az összes késés-perc (igazolt + igazolatlan) – a „késésből" oszlop zárójeles száma. */
  kesesPerc(k?: OfKeses | null): number {
    return (k?.perc_igazolatlan || 0) + (k?.perc_igazolt || 0);
  }

  /* =====================================================================
   *  NÉZETVÁLTÁS – Hiányzások / Jegyek / Tanórai feljegyzések
   *
   *  A két új nézet LUSTÁN töltődik: csak ha az osztályfőnök rájuk vált, és csak ha az
   *  osztály azóta változott. A hiányzás-összesítő marad az alapnézet – azt eddig is így
   *  nyitották meg, a megszokott első képernyő nem változik.
   * ===================================================================== */
  nezet: OfNezet = 'hianyzas';
  sajatIntezmeny: any = null;

  /** Az igazolás-előzmény ablak tanulója (of-igazolas-elozmeny, backend: hianyzas-igazolas.js › /elozmeny). */
  elozmenyTanulo: { oktazon: string; nev: string } | null = null;
  elozmenyNyit(t: { oktazon: string; tanulo_nev?: string }): void {
    this.elozmenyTanulo = { oktazon: String(t.oktazon), nev: String(t.tanulo_nev || '') };
  }

  nezetValt(ertek: any): void {
    const n = String(ertek || '') as OfNezet;
    this.nezet = (['hianyzas', 'elbiralas', 'jegyek', 'ikszek', 'szakvelemeny', 'minosithetoseg', 'lemorzsolodas', 'kesve'] as OfNezet[]).includes(n) ? n : 'hianyzas';
    this.nezetFrissit();
  }

  private nezetFrissit(): void {
    if (!this.kivOsztalyId) return;
    if (this.nezet === 'jegyek' && this.jegyOsztalyId !== this.kivOsztalyId) this.loadJegyek();
    if (this.nezet === 'ikszek' && this.ikszOsztalyId !== this.kivOsztalyId) this.loadIkszek();
    if (this.nezet === 'szakvelemeny' && this.szvOsztalyId !== this.kivOsztalyId) this.loadSzv();
  }

  /* =====================================================================
   *  SNI / BTMN – SZAKÉRTŐI VÉLEMÉNYEK (backend/szakertoi-velemeny.js)
   *
   *  A 20/2012. EMMI r. 95. § (5) adatai a beírási naplóhoz. A szülő a szakértői véleményt
   *  többnyire az osztályfőnöknek adja le, ezért ő is rögzítheti és módosíthatja – de csak a
   *  saját osztálya tanulóinál (a kiszolgáló dönt, idegen tanulóra 403). A törlés adminé; a
   *  `jog` mezőt a kiszolgáló adja, a gombok ehhez igazodnak.
   * ===================================================================== */
  szvAdat: { tanulok: OfSzvTanulo[]; jog: { iras: boolean; torles: boolean } } | null = null;
  szvOsztalyId = '';
  loadingSzv = false;
  szvHiba = '';
  szvCsakErintett = false;
  szvNyitott: string | null = null;
  szvUrlap: OfSzakvelemeny | null = null;
  szvUrlapOkt = '';
  szvMentes = false;

  loadSzv(): void {
    if (!this.kivOsztalyId) return;
    const osztalyId = this.kivOsztalyId;
    this.loadingSzv = true;
    this.szvHiba = '';
    this.http.get<any>(`/api/szakertoi-velemeny/osztaly/${encodeURIComponent(osztalyId)}/${this.intezmenyPath}`).subscribe({
      next: (rsp) => {
        if (osztalyId !== this.kivOsztalyId) return;
        this.szvAdat = {
          tanulok: (rsp?.tanulok || []).map((t: any) => ({
            oktazon: String(t.oktazon), tanulo_nev: t.tanulo_nev || String(t.oktazon),
            velemenyek: t.velemenyek || [], kreta: t.kreta || [],
          })),
          jog: { iras: !!rsp?.jog?.iras, torles: !!rsp?.jog?.torles },
        };
        this.szvOsztalyId = osztalyId;
        if (this.szvNyitott && !this.szvAdat.tanulok.some((t) => t.oktazon === this.szvNyitott)) this.szvNyitott = null;
        this.loadingSzv = false;
      },
      error: (err) => {
        if (osztalyId !== this.kivOsztalyId) return;
        this.loadingSzv = false;
        this.szvAdat = null;
        this.szvOsztalyId = '';
        this.szvHiba = err?.error?.error || 'Nem sikerült betölteni a szakértői véleményeket.';
      },
    });
  }

  get szvTanulok(): OfSzvTanulo[] {
    const l = this.szvAdat?.tanulok || [];
    return this.szvCsakErintett ? l.filter((t) => t.velemenyek.length || t.kreta.length) : l;
  }

  szvTipusai(t: OfSzvTanulo): string[] {
    return [...new Set(t.velemenyek.map((v) => String(v.tipus || '')).filter(Boolean))];
  }

  szvKretaTipusai(t: OfSzvTanulo): string {
    return [...new Set(t.kreta.map((v) => String(v.tipus || '')).filter(Boolean))].join(', ');
  }

  /** A legutóbbi (kelt szerint utolsó) vélemény következő felülvizsgálata, vagy az érvényessége. */
  szvKovetkezo(t: OfSzvTanulo): string {
    const v = t.velemenyek[t.velemenyek.length - 1];
    return String(v?.kovetkezo_felulvizsgalat || v?.ervenyesseg || '');
  }

  szvNyit(oktazon: string): void {
    this.szvNyitott = this.szvNyitott === oktazon ? null : oktazon;
    if (this.szvUrlapOkt !== this.szvNyitott) this.szvMegse();
  }

  /** Új vélemény – üresen, vagy a KRÉTA-adatból előtöltve (`forras` = 'kreta'). */
  szvUj(t: OfSzvTanulo, kreta: OfSzakvelemeny | null = null): void {
    this.szvUrlapOkt = t.oktazon;
    this.szvUrlap = kreta
      ? { ...kreta, felulvizsgalat: false, forras: 'kreta' }
      : { tipus: 'SNI', felulvizsgalat: t.velemenyek.length > 0, forras: 'sajat' };
  }

  szvSzerkeszt(t: OfSzvTanulo, v: OfSzakvelemeny): void {
    this.szvUrlapOkt = t.oktazon;
    this.szvUrlap = { ...v, felulvizsgalat: !!Number(v.felulvizsgalat) };
  }

  szvMegse(): void {
    this.szvUrlap = null;
    this.szvUrlapOkt = '';
  }

  szvMent(): void {
    const u = this.szvUrlap;
    if (!u || !this.szvUrlapOkt) return;
    if (!String(u.bizottsag_nev || '').trim() && !String(u.velemeny_szam || '').trim()) {
      this.data.sendToast('Legalább a kiállító bizottság nevét vagy a vélemény számát add meg.', 'warning');
      return;
    }
    const keres = u.id
      ? this.http.put<any>(`/api/szakertoi-velemeny/${u.id}/${this.intezmenyPath}`, u)
      : this.http.post<any>(`/api/szakertoi-velemeny/${this.intezmenyPath}`, { ...u, oktazon: this.szvUrlapOkt });
    this.szvMentes = true;
    keres.subscribe({
      next: () => {
        this.szvMentes = false;
        this.data.sendToast('A szakértői vélemény mentve.', 'success');
        this.szvMegse();
        this.loadSzv();
      },
      error: (e) => {
        this.szvMentes = false;
        this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
      },
    });
  }

  /** Törlés (jelölés) – csak adminnak látszik; a kiszolgáló is csak adminnak engedi. */
  szvTorol(v: OfSzakvelemeny): void {
    if (!v?.id || !confirm(`Törlöd a(z) ${v.tipus} szakértői véleményt (${v.velemeny_szam || v.bizottsag_nev || ''})?`)) return;
    this.http.delete<any>(`/api/szakertoi-velemeny/${v.id}/${this.intezmenyPath}`).subscribe({
      next: () => { this.data.sendToast('Törölve.', 'success'); this.loadSzv(); },
      error: (e) => this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error'),
    });
  }

  private get atlagTizedes(): number {
    const raw = Number(this.sajatIntezmeny?.ertekelesAtlagTizedes ?? this.sajatIntezmeny?.ertekeles_atlag_tizedes ?? 1);
    return Number.isFinite(raw) ? Math.min(3, Math.max(0, Math.trunc(raw))) : 1;
  }

  /* =====================================================================
   *  AZ OSZTÁLY JEGYEI
   *
   *  „Osztályfőnökként hogy látjuk a diákok előmenetelét, jegyeit?" – eddig sehogy: a jegyet
   *  a szaktanár a saját órájáról, a szülő a saját gyerekéről látta. Itt egy táblázatban
   *  áll az osztály minden tanulójának minden tárgyból kapott jegye (a csoportbontásos,
   *  más osztállyal közös órákon kapottak is), tantárgyanként átlaggal.
   *
   *  A mátrixot EGYSZER építjük fel a betöltéskor és szűrőváltáskor – a sablonban futó
   *  számolás egy év végi, több ezer jegyes osztálynál minden egérmozdulatra újraszámolna.
   * ===================================================================== */
  jegyAdat: any = null;
  jegyOsztalyId = '';
  loadingJegyek = false;
  jegyHiba = '';
  jegyIdoszak: OfIdoszak = 'ev';
  uresTargyakRejtve = false;
  private jegyLista: OfJegy[] = [];
  jegyOszlopok: OfTargyOszlop[] = [];
  jegySorok: OfJegySor[] = [];
  jegyLabAtlag: Record<string, string> = {};
  jegyTipusok: { nev: string; szin: string | null }[] = [];

  loadJegyek(): void {
    if (!this.kivOsztalyId) return;
    const osztalyId = this.kivOsztalyId;
    this.loadingJegyek = true;
    this.jegyHiba = '';
    this.http.get<any>(`/api/of/jegyek/${this.intezmenyPath}?${this.osztalyQuery()}`).subscribe({
      next: (rsp) => {
        if (osztalyId !== this.kivOsztalyId) return;   // közben másik osztályt választottak
        this.jegyAdat = rsp || null;
        this.jegyOsztalyId = osztalyId;
        this.jegyLista = jegyekKiegeszit(rsp);
        this.jegyMatrixEpit();
        this.loadingJegyek = false;
      },
      error: (err) => {
        if (osztalyId !== this.kivOsztalyId) return;
        this.loadingJegyek = false;
        this.jegyAdat = null;
        this.jegyOsztalyId = '';
        this.jegyHiba = err?.error?.error || 'Nem sikerült betölteni az osztály jegyeit.';
      },
    });
  }

  private idoszakbaEsik(j: OfJegy): boolean {
    if (this.jegyIdoszak === 'ev') return true;
    //  A záró értékelés a saját félévéhez tartozik, bármikor írták is be.
    if (j.zaro) return this.jegyIdoszak === 'f1' ? j.zaro === 'F' : j.zaro === 'É';
    return j.felev === this.jegyIdoszak;
  }

  jegyMatrixEpit(): void {
    const adat = this.jegyAdat;
    if (!adat) { this.jegyOszlopok = []; this.jegySorok = []; this.jegyLabAtlag = {}; this.jegyTipusok = []; return; }
    const tizedes = this.atlagTizedes;
    const lathato = this.jegyLista.filter((j) => this.idoszakbaEsik(j));

    const tanuloTargy = new Map<string, Map<string, OfJegy[]>>();
    const targyJegyei = new Map<string, OfJegy[]>();
    const tipusok = new Map<string, string | null>();
    for (const j of lathato) {
      const tk = String(j.tantargy_id);
      if (!tanuloTargy.has(j.oktazon)) tanuloTargy.set(j.oktazon, new Map());
      const m = tanuloTargy.get(j.oktazon)!;
      if (!m.has(tk)) m.set(tk, []);
      m.get(tk)!.push(j);
      if (!targyJegyei.has(tk)) targyJegyei.set(tk, []);
      targyJegyei.get(tk)!.push(j);
      if (j.emnev && !j.zaro && !tipusok.has(j.emnev)) tipusok.set(j.emnev, j.szinCss ?? null);
    }

    const osszes: OfTargyOszlop[] = adat.tantargyak || [];
    this.jegyOszlopok = this.uresTargyakRejtve ? osszes.filter((t) => targyJegyei.has(String(t.tantargy_id))) : osszes;

    this.jegySorok = (adat.tanulok || []).map((t: any) => {
      const sajat = tanuloTargy.get(String(t.oktazon)) || new Map<string, OfJegy[]>();
      const cellak: Record<string, OfJegyCella> = {};
      const targyAtlagok: number[] = [];
      let jegyDb = 0;
      let gyengeTargyak = 0;
      for (const o of this.jegyOszlopok) {
        const lista = sajat.get(String(o.tantargy_id)) || [];
        const atlag = atlagSzamol(lista);
        const gyenge = gyengeAtlag(atlag);
        cellak[o.tantargy_id] = {
          evkozi: lista.filter((j) => !j.zaro),
          zaro: lista.filter((j) => !!j.zaro),
          atlag, atlagSzoveg: atlagSzoveg(atlag, tizedes), gyenge,
        };
        jegyDb += lista.length;
        if (gyenge) gyengeTargyak++;
        //  Az átlagok átlagába csak az érdemjegyes tárgy megy: a százalék más skála.
        if (atlag.atlag !== null && !atlag.szazalek) targyAtlagok.push(atlag.atlag);
      }
      const ossz = targyAtlagok.length
        ? atlagSzoveg({ atlag: targyAtlagok.reduce((s, x) => s + x, 0) / targyAtlagok.length, db: targyAtlagok.length, szazalek: false }, tizedes)
        : '';
      return { oktazon: String(t.oktazon), tanulo_nev: t.tanulo_nev, cellak, jegyDb, osszAtlag: ossz, gyengeTargyak };
    });

    this.jegyLabAtlag = {};
    for (const o of this.jegyOszlopok) {
      this.jegyLabAtlag[o.tantargy_id] = atlagSzoveg(atlagSzamol(targyJegyei.get(String(o.tantargy_id)) || []), tizedes);
    }
    this.jegyTipusok = [...tipusok.entries()]
      .map(([nev, szin]) => ({ nev, szin }))
      .sort((a, b) => a.nev.localeCompare(b.nev, 'hu'));
    if (this.jegyNezet === 'tanulo') {
      if (!this.jegySorok.some((s) => s.oktazon === this.tanuloNezetOkt)) this.tanuloNezetOkt = this.jegySorok[0]?.oktazon || '';
      this.tanuloNezetEpit();
    }
  }

  /* =====================================================================
   *  TANULÓNKÉNTI NÉZET (user, 2026-10-14: „mint a KRÉTÁ-ban, az átláthatóság miatt”).
   *
   *  Egy tanuló jegyei: sor a tantárgy, oszlop a hónap (szeptembertől júniusig), a félévi és
   *  az év végi értékelés a saját oszlopában, a végén a súlyozott átlag. Ugyanabból a már
   *  betöltött listából épül, mint az osztálytáblázat – nincs új kérés.
   * ===================================================================== */
  jegyNezet: 'osztaly' | 'tanulo' = 'osztaly';
  tanuloNezetOkt = '';
  readonly tanuloNezetOszlopok: { k: string; n: string; zaro?: boolean }[] = [
    { k: '09', n: 'Szept.' }, { k: '10', n: 'Okt.' }, { k: '11', n: 'Nov.' }, { k: '12', n: 'Dec.' },
    { k: '01', n: 'Jan.' }, { k: 'F', n: 'Félévi', zaro: true },
    { k: '02', n: 'Febr.' }, { k: '03', n: 'Márc.' }, { k: '04', n: 'Ápr.' }, { k: '05', n: 'Máj.' },
    { k: '06', n: 'Jún.' }, { k: 'É', n: 'Év végi', zaro: true },
  ];
  tanuloNezetSorok: { tantargy_id: string; tantargy_nev: string; cellak: Record<string, OfJegy[]>;
    atlag: string; gyenge: boolean; db: number }[] = [];

  jegyNezetValt(n: 'osztaly' | 'tanulo'): void {
    this.jegyNezet = n;
    if (n === 'tanulo') {
      if (!this.jegySorok.some((s) => s.oktazon === this.tanuloNezetOkt)) this.tanuloNezetOkt = this.jegySorok[0]?.oktazon || '';
      this.tanuloNezetEpit();
    }
  }

  tanuloValaszt(okt: string): void { this.tanuloNezetOkt = okt; this.tanuloNezetEpit(); }

  tanuloLeptet(irany: number): void {
    const i = this.jegySorok.findIndex((s) => s.oktazon === this.tanuloNezetOkt);
    const uj = this.jegySorok[i + irany];
    if (uj) this.tanuloValaszt(uj.oktazon);
  }

  get tanuloNezetIndex(): number { return this.jegySorok.findIndex((s) => s.oktazon === this.tanuloNezetOkt); }
  get tanuloNezetSor(): OfJegySor | null { return this.jegySorok.find((s) => s.oktazon === this.tanuloNezetOkt) || null; }

  tanuloNezetEpit(): void {
    const okt = this.tanuloNezetOkt;
    const tizedes = this.atlagTizedes;
    const sajat = this.jegyLista.filter((j) => j.oktazon === okt && this.idoszakbaEsik(j));
    const kulcs = (j: OfJegy) => {
      if (j.zaro) return j.zaro;
      const h = String(j.datum || '').slice(5, 7);
      //  A nyári (július–augusztus) jegy – pl. javítóvizsga – a júniusi oszlopba kerül.
      return h === '07' || h === '08' ? '06' : h;
    };
    this.tanuloNezetSorok = this.jegyOszlopok.map((o) => {
      const lista = sajat.filter((j) => String(j.tantargy_id) === String(o.tantargy_id))
        .sort((a, b) => String(a.datum).localeCompare(String(b.datum)));
      const cellak: Record<string, OfJegy[]> = {};
      for (const j of lista) { const k = kulcs(j); (cellak[k] = cellak[k] || []).push(j); }
      const atlag = atlagSzamol(lista);
      return { tantargy_id: String(o.tantargy_id), tantargy_nev: o.tantargy_nev, cellak,
        atlag: atlagSzoveg(atlag, tizedes), gyenge: gyengeAtlag(atlag), db: lista.length };
    });
  }

  get jegyNelkuliTanulok(): number {
    return this.jegySorok.filter((s) => !s.jegyDb).length;
  }

  trackTargy = (_: number, o: OfTargyOszlop) => o.tantargy_id;
  trackTanulo = (_: number, s: { oktazon: string }) => s.oktazon;
  trackJegy = (_: number, j: OfJegy) => j.id;

  /*  EGY TANULÓ JEGYEI RÉSZLETESEN – a névre kattintva.
   *
   *  A táblázat cellájában csak a jegy fér el; a részletben tantárgyanként félévre bontva
   *  látszik minden, és alatta időrendben a téma, a típus, a súly és a tanár is. Az adat a
   *  már betöltött listából jön, nincs új kérés. */
  reszletSor: OfJegySor | null = null;
  reszletTargyak: {
    tantargy_nev: string; f1: OfJegy[]; felevi: OfJegy[]; f1Atlag: string;
    f2: OfJegy[]; evvegi: OfJegy[]; evesAtlag: string; gyenge: boolean;
  }[] = [];
  reszletIdorend: (OfJegy & { tantargy_nev: string })[] = [];

  reszletNyit(sor: OfJegySor): void {
    const tizedes = this.atlagTizedes;
    const sajat = this.jegyLista.filter((j) => j.oktazon === sor.oktazon);
    const nevek = new Map<string, string>((this.jegyAdat?.tantargyak || []).map((t: any) => [String(t.tantargy_id), t.tantargy_nev]));
    const targyankent = new Map<string, OfJegy[]>();
    for (const j of sajat) {
      const k = String(j.tantargy_id);
      if (!targyankent.has(k)) targyankent.set(k, []);
      targyankent.get(k)!.push(j);
    }
    this.reszletTargyak = [...targyankent.entries()]
      .map(([id, lista]) => {
        const f1 = lista.filter((j) => !j.zaro && j.felev === 'f1');
        const eves = atlagSzamol(lista);
        return {
          tantargy_nev: nevek.get(id) || 'Ismeretlen tantárgy',
          f1,
          felevi: lista.filter((j) => j.zaro === 'F'),
          f1Atlag: atlagSzoveg(atlagSzamol(f1), tizedes),
          f2: lista.filter((j) => !j.zaro && j.felev === 'f2'),
          evvegi: lista.filter((j) => j.zaro === 'É'),
          evesAtlag: atlagSzoveg(eves, tizedes),
          gyenge: gyengeAtlag(eves),
        };
      })
      .sort((a, b) => a.tantargy_nev.localeCompare(b.tantargy_nev, 'hu'));
    this.reszletIdorend = sajat
      .map((j) => ({ ...j, tantargy_nev: nevek.get(String(j.tantargy_id)) || 'Ismeretlen tantárgy' }))
      .sort((a, b) => b.datum.localeCompare(a.datum));
    this.reszletSor = sor;
  }

  reszletBezar(): void {
    this.reszletSor = null;
    this.reszletTargyak = [];
    this.reszletIdorend = [];
  }

  /* =====================================================================
   *  „IKSZES ÓRÁK" – TANÓRAI JELÖLÉSEK ÉS FELJEGYZÉSEK
   *
   *  A KRÉTA-ban az osztályfőnök a Feljegyzések között látta, ha a tanár az órán X-szel
   *  jelölte a tanulót (felszereléshiány, házi feladat hiány). A naplóban ezek az óra
   *  névsorának gombjai; a szöveges feljegyzések külön táblában vannak. Itt a kettő együtt
   *  látszik: tanulónként típusonkénti darabszám és időrendi lista.
   *
   *  Hogy mi számít „ikszes"-nek, azt az osztályfőnök dönti el: alapból a felszerelés- és a
   *  házi feladat hiány van kijelölve, de minden típus választható – a választást a
   *  böngésző megjegyzi (csak kényelmi beállítás, a kiszolgálóra nem kerül).
   * ===================================================================== */
  private static readonly IKSZ_TAROLO = 'of-ikszes-tipusok';
  ikszAdat: any = null;
  ikszOsztalyId = '';
  loadingIkszek = false;
  ikszHiba = '';
  ikszTipusok: OfIkszTipus[] = [];
  ikszKivalasztott = new Set<string>();
  ikszTol = '';
  ikszIg = '';
  ikszTanulo = '';
  ikszTipusDb: Record<string, number> = {};
  ikszOszlopok: OfIkszTipus[] = [];
  ikszSorok: { oktazon: string; tanulo_nev: string; db: Record<string, number>; ossz: number }[] = [];
  ikszLista: OfIkszBejegyzes[] = [];
  private ikszTipusMap = new Map<string, OfIkszTipus>();

  loadIkszek(): void {
    if (!this.kivOsztalyId) return;
    const osztalyId = this.kivOsztalyId;
    this.loadingIkszek = true;
    this.ikszHiba = '';
    this.http.get<any>(`/api/of/tanorai-feljegyzesek/${this.intezmenyPath}?${this.osztalyQuery()}`).subscribe({
      next: (rsp) => {
        if (osztalyId !== this.kivOsztalyId) return;
        this.ikszAdat = rsp || null;
        this.ikszOsztalyId = osztalyId;
        this.ikszTipusok = rsp?.tipusok || [];
        this.ikszTipusMap = new Map(this.ikszTipusok.map((t) => [t.kulcs, t]));
        const nevek = new Map<string, string>((rsp?.tanulok || []).map((t: any) => [String(t.oktazon), t.tanulo_nev]));
        for (const b of (rsp?.bejegyzesek || []) as OfIkszBejegyzes[]) b.tanulo_nev = nevek.get(String(b.oktazon)) || b.oktazon;
        this.ikszKivalasztottBetolt();
        if (this.ikszTanulo && !nevek.has(this.ikszTanulo)) this.ikszTanulo = '';
        this.ikszSzur();
        this.loadingIkszek = false;
      },
      error: (err) => {
        if (osztalyId !== this.kivOsztalyId) return;
        this.loadingIkszek = false;
        this.ikszAdat = null;
        this.ikszOsztalyId = '';
        this.ikszHiba = err?.error?.error || 'Nem sikerült betölteni a tanórai feljegyzéseket.';
      },
    });
  }

  private ikszKivalasztottBetolt(): void {
    let mentett: string[] | null = null;
    try {
      const nyers = localStorage.getItem(OsztalyfonokComponent.IKSZ_TAROLO);
      mentett = nyers ? JSON.parse(nyers) : null;
    } catch { mentett = null; }
    const ismert = new Set(this.ikszTipusok.map((t) => t.kulcs));
    this.ikszKivalasztott = Array.isArray(mentett)
      ? new Set(mentett.filter((k) => ismert.has(k)))
      : new Set(this.ikszTipusok.filter((t) => t.ikszes).map((t) => t.kulcs));
  }

  private ikszKivalasztottMent(): void {
    try {
      localStorage.setItem(OsztalyfonokComponent.IKSZ_TAROLO, JSON.stringify([...this.ikszKivalasztott]));
    } catch { /* privát mód / tiltott tároló: a választás csak erre a megnyitásra szól */ }
  }

  ikszTipusValt(kulcs: string): void {
    if (this.ikszKivalasztott.has(kulcs)) this.ikszKivalasztott.delete(kulcs);
    else this.ikszKivalasztott.add(kulcs);
    this.ikszKivalasztottMent();
    this.ikszSzur();
  }

  ikszTipusCsoport(mit: 'ikszes' | 'mind' | 'semmi'): void {
    this.ikszKivalasztott = new Set(
      mit === 'semmi' ? [] : this.ikszTipusok.filter((t) => mit === 'mind' || t.ikszes).map((t) => t.kulcs));
    this.ikszKivalasztottMent();
    this.ikszSzur();
  }

  ikszTanuloValt(oktazon: string): void {
    this.ikszTanulo = this.ikszTanulo === oktazon ? '' : oktazon;
    this.ikszSzur();
  }

  ikszSzur(): void {
    const osszes: OfIkszBejegyzes[] = this.ikszAdat?.bejegyzesek || [];
    const tol = String(this.ikszTol || '').slice(0, 10);
    const ig = String(this.ikszIg || '').slice(0, 10);
    const idoben = osszes.filter((b) => (!tol || b.datum >= tol) && (!ig || b.datum <= ig));

    //  A típus-chipek száma az időszakra szól, a kijelöléstől függetlenül – így látszik,
    //  melyik még ki nem jelölt típusban van valami.
    this.ikszTipusDb = {};
    for (const b of idoben) this.ikszTipusDb[b.kulcs] = (this.ikszTipusDb[b.kulcs] || 0) + 1;

    const szurt = idoben.filter((b) => this.ikszKivalasztott.has(b.kulcs));
    //  Oszlop csak abból a kijelölt típusból lesz, amiben az időszakban van bejegyzés – a
    //  „mind" választásnál különben 15 üres oszlop nyomná el a lényeget. Ha egyikben sincs,
    //  a kijelölt tanórai jelölések oszlopai maradnak (nullákkal), hogy a táblázat ne tűnjön el.
    const kijelolt = this.ikszTipusok.filter((t) => this.ikszKivalasztott.has(t.kulcs));
    const vanBenne = kijelolt.filter((t) => this.ikszTipusDb[t.kulcs]);
    this.ikszOszlopok = vanBenne.length ? vanBenne : kijelolt.filter((t) => t.forras === 'ora');
    const sorok = new Map<string, { oktazon: string; tanulo_nev: string; db: Record<string, number>; ossz: number }>();
    for (const t of (this.ikszAdat?.tanulok || [])) {
      sorok.set(String(t.oktazon), { oktazon: String(t.oktazon), tanulo_nev: t.tanulo_nev, db: {}, ossz: 0 });
    }
    for (const b of szurt) {
      const s = sorok.get(String(b.oktazon));
      if (!s) continue;
      s.db[b.kulcs] = (s.db[b.kulcs] || 0) + 1;
      s.ossz++;
    }
    this.ikszSorok = [...sorok.values()];
    this.ikszLista = this.ikszTanulo ? szurt.filter((b) => String(b.oktazon) === this.ikszTanulo) : szurt;
  }

  ikszSzuroTorol(): void {
    this.ikszTol = '';
    this.ikszIg = '';
    this.ikszTanulo = '';
    this.ikszSzur();
  }

  ikszTipus(kulcs: string): OfIkszTipus | undefined {
    return this.ikszTipusMap.get(kulcs);
  }

  get ikszTanuloNev(): string {
    return this.ikszSorok.find((s) => s.oktazon === this.ikszTanulo)?.tanulo_nev || '';
  }

  get ikszOsszesen(): number {
    return this.ikszSorok.reduce((s, x) => s + x.ossz, 0);
  }

  /** A típus színe a chipen: a hiány piros, a dicséret zöld, a felmentés sárga. */
  ikszSzin(kulcs: string): string {
    if (kulcs === 'tanorai_dicseret' || /dics[ée]ret/i.test(kulcs)) return 'success';
    if (kulcs === 'felmentes') return 'warning';
    if (kulcs === 'felszereles_hiany' || kulcs === 'hazi_feladat_hiany' || /int[őo]|figyelmeztet/i.test(kulcs)) return 'danger';
    return 'medium';
  }


  /* =====================================================================
   *  IGAZOLATLAN MULASZTÁS – ÉRTESÍTÉSEK (backend/mulasztas-ertesites.js)
   *
   *  A 20/2012. EMMI r. 51. § szerinti értesítések (1, 10, 30, 50 óra) és a nem tanköteles
   *  tanuló két írásbeli figyelmeztetése (50. § (4)): ki érte el a küszöböt, melyik címzett felé
   *  kell még értesítés, és az értesítés rögzítése (dátum, mód, címzett). A nyomtatvány a
   *  meglévő úton (/api/nyomtatvany/…) készül, aláírva és tárolva. A kiszolgáló dönt a jogról:
   *  csak a saját osztály (osztályfőnök, helyettes) vagy admin.
   * ===================================================================== */
  iglAdat: { tanulok: OfIglTanulo[]; allapot?: string; jog?: { admin: boolean }; tanev?: number | null } | null = null;
  iglFut = false;
  iglHiba = '';
  iglForm: OfIglForm | null = null;
  iglMentes = false;
  iglKodok: { cimzettek: { kod: string; nev: string }[]; modok: { kod: string; nev: string }[] } = { cimzettek: [], modok: [] };

  loadIgl(): void {
    if (!this.kivOsztalyId) { this.iglAdat = null; return; }
    this.iglFut = true;
    this.iglHiba = '';
    if (!this.iglKodok.cimzettek.length) {
      this.http.get<any>('/api/mulasztas-ertesites/kodok').subscribe({
        next: (k) => { this.iglKodok = { cimzettek: k?.cimzettek || [], modok: k?.modok || [] }; },
        error: () => { /* a lista nélkül is megy, csak a választó üres */ },
      });
    }
    const ev = this.tanev ? `?tanev=${this.tanev}` : '';
    this.http.get<any>(`/api/mulasztas-ertesites/osztaly/${encodeURIComponent(this.kivOsztalyId)}/${this.intezmenyPath}${ev}`).subscribe({
      next: (rsp) => {
        this.iglAdat = { tanulok: rsp?.tanulok || [], allapot: rsp?.allapot, jog: rsp?.jog, tanev: Number(rsp?.tanev?.ev) || null };
        this.iglFut = false;
      },
      error: (e) => { this.iglAdat = null; this.iglFut = false; this.iglHiba = e?.error?.error || 'Az igazolatlan mulasztások nem töltődtek be.'; },
    });
  }

  /** 'ÉÉÉÉ-HH-NN' → 'ÉÉÉÉ. HH. NN.' (a 'hu' date pipe nincs regisztrálva). */
  iglNap(s: string | null | undefined): string {
    const v = String(s || '').slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v.slice(0, 4)}. ${v.slice(5, 7)}. ${v.slice(8, 10)}.` : '';
  }

  get iglNyitottDb(): number {
    return (this.iglAdat?.tanulok || []).filter((t) => t.nyitott > 0).length;
  }

  iglNyomtatvany(td: OfIglTeendo): void {
    if (td.nyomtatvany) this.data.megnyitFajl(td.nyomtatvany.url, td.nyomtatvany.cim);
  }

  /** `fajta`: a teendő fajtájától eltérő zárás (az 51. § (3) teendőjénél: 'ertesites_eredmenyes'). */
  iglRogzitNyit(t: OfIglTanulo, td: OfIglTeendo, cimzett: string, fajta?: string): void {
    const f = fajta || td.fajta;
    this.iglForm = {
      oktazon: t.oktazon, fajta: f, kuszob: td.kuszob,
      cim: f === 'ertesites_eredmenyes' ? 'Az értesítés eredményes volt – ismételt megkeresés nem kell (51. § (3))' : td.cim,
      cimzett, mod: 'posta', datum: new Date().toISOString().slice(0, 10), cimzett_nev: '', megjegyzes: '',
    };
  }

  iglMent(): void {
    const f = this.iglForm;
    if (!f || this.iglMentes) return;
    if (!f.cimzett || !f.mod || !f.datum) { this.data.sendToast('A címzett, a mód és a dátum kötelező.', 'warning'); return; }
    this.iglMentes = true;
    this.http.post<any>(`/api/mulasztas-ertesites/${this.intezmenyPath}`, {
      oktazon: f.oktazon, tanev: this.iglAdat?.tanev || this.tanev, fajta: f.fajta, kuszob: f.kuszob, cimzett: f.cimzett, mod: f.mod,
      datum: f.datum, cimzett_nev: f.cimzett_nev || undefined, megjegyzes: f.megjegyzes || undefined,
    }).subscribe({
      next: (r) => {
        this.iglMentes = false;
        this.iglForm = null;
        this.data.sendToast(r?.dokumentum_id ? 'Rögzítve, a kiadott nyomtatványhoz kötve.' : 'Rögzítve.');
        this.loadIgl();
      },
      error: (e) => { this.iglMentes = false; this.data.sendToast(e?.error?.error || 'A rögzítés nem sikerült.', 'error'); },
    });
  }

  async iglVisszavon(e: OfIglErtesites): Promise<void> {
    const a = await this.alertCtrl.create({
      header: 'Rögzítés visszavonása',
      message: 'A sor megmarad, de visszavontként jelölve. Mi a visszavonás oka?',
      inputs: [{ name: 'indok', type: 'text', placeholder: 'pl. téves tanuló, téves dátum' }],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Visszavonás', role: 'ok' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    const indok = String(data?.values?.indok || '').trim();
    if (role !== 'ok') return;
    if (!indok) { this.data.sendToast('A visszavonás oka kötelező.', 'warning'); return; }
    this.http.post<any>(`/api/mulasztas-ertesites/${encodeURIComponent(e.id)}/visszavon/${this.intezmenyPath}`, { indok }).subscribe({
      next: () => { this.data.sendToast('Visszavonva.'); this.loadIgl(); },
      error: (er) => this.data.sendToast(er?.error?.error || 'A visszavonás nem sikerült.', 'error'),
    });
  }

}
